import type { McpToolDef } from '../types';

/** Extract all {{var}} template variables from a string. */
function extractTemplateVars(text: string): string[] {
  if (!text) return [];
  const vars = new Set<string>();
  for (const m of text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
    vars.add(m[1]);
  }
  return Array.from(vars);
}

/** Validate that all template variables in path/queryMap/bodyTemplate have matching inputSchema properties. */
function getToolTemplateError(tool: McpToolDef): string | null {
  const schema = tool.inputSchema;
  const fieldNames = new Set<string>(
    schema?.properties ? Object.keys(schema.properties as Record<string, unknown>) : []
  );

  // Collect template variables from path
  const pathOnly = (tool.path || '').split('?')[0];
  const pathVars = extractTemplateVars(pathOnly);

  // Collect from dynamic query params
  const queryVars: string[] = [];
  if (Array.isArray(tool.queryMap)) {
    for (const q of tool.queryMap) {
      if (q.dynamic) queryVars.push(...extractTemplateVars(q.value || ''));
    }
  }

  // Collect from body template (POST/PUT/PATCH only)
  const method = (tool.httpMethod || 'GET').toUpperCase();
  const bodyVars = method !== 'GET' ? extractTemplateVars(tool.bodyTemplate || '') : [];

  const allVars = [...new Set([...pathVars, ...queryVars, ...bodyVars])];
  const missing = allVars.filter(v => !fieldNames.has(v));

  if (missing.length > 0) {
    return `Missing input field${missing.length > 1 ? 's' : ''} for template variable${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`;
  }

  return null;
}

/** Check if a tool has no input parameters (empty or missing schema). */
export function isParameterlessTool(tool: McpToolDef): boolean {
  const schema = tool.inputSchema;
  if (!schema || typeof schema !== 'object') return true;
  const props = schema.properties as Record<string, unknown> | undefined;
  return !props || Object.keys(props).length === 0;
}

/** Check if a tool is valid for use in the chat widget. Returns true if valid. */
export function isToolValid(tool: McpToolDef): boolean {
  const schema = tool.inputSchema;
  // Allow tools with no inputSchema (parameterless tools)
  if (!schema || typeof schema !== 'object') {
    // Still reject if there's a validation error
    return !tool.annotations?.validationError;
  }
  if (schema.type && schema.type !== 'object') return false;
  if (tool.annotations?.validationError) return false;
  if (getToolTemplateError(tool)) return false;
  return true;
}

/** Filter tools to only valid, chat-ready ones. Logs warnings for skipped tools. */
export function filterValidTools(tools: McpToolDef[]): McpToolDef[] {
  return tools.filter(t => {
    if (t.annotations?.validationError) {
      console.warn(`[mcp-chat-widget] Skipping tool "${t.name}": ${t.annotations.validationError}`);
      return false;
    }
    const schema = t.inputSchema;
    // Allow tools with no inputSchema (parameterless tools)
    if (!schema || typeof schema !== 'object') {
      return true;
    }
    if (schema.type && schema.type !== 'object') {
      console.warn(`[mcp-chat-widget] Skipping tool "${t.name}": inputSchema type is "${schema.type}", expected "object"`);
      return false;
    }
    const templateError = getToolTemplateError(t);
    if (templateError) {
      console.warn(`[mcp-chat-widget] Skipping tool "${t.name}": ${templateError}`);
      return false;
    }
    return true;
  });
}
